import { calculateLayoutWidth, calculateLayoutHeight } from './layoutGeometry';

export const createRectDifferenceRects = (outerRect, innerRect) => {
  const outerRight = outerRect.x + outerRect.width; // stores the right edge of the outer rectangle
  const outerBottom = outerRect.y + outerRect.height; // stores the bottom edge of the outer rectangle
  const innerRight = innerRect.x + innerRect.width; // stores the right edge of the rectangle that should be removed
  const innerBottom = innerRect.y + innerRect.height; // stores the bottom edge of the rectangle that should be removed

  const overlaps =
    outerRect.x < innerRight &&
    outerRight > innerRect.x &&
    outerRect.y < innerBottom &&
    outerBottom > innerRect.y; // checks whether the two rectangles touch or overlap

  if (!overlaps) {
    return [outerRect]; // if nothing overlaps, the full outer rectangle stays visible
  }

  const rects = []; // stores the remaining visible rectangle pieces

  const topHeight = Math.min(innerRect.y, outerBottom) - outerRect.y; // calculates the visible strip above the inner rectangle

  if (topHeight > 0) {
    rects.push({
      x: outerRect.x,
      y: outerRect.y,
      width: outerRect.width,
      height: topHeight,
    }); // adds the top visible strip
  }

  const bottomY = Math.max(innerBottom, outerRect.y); // finds where the bottom visible strip starts
  const bottomHeight = outerBottom - bottomY; // calculates the visible strip below the inner rectangle

  if (bottomHeight > 0) {
    rects.push({
      x: outerRect.x,
      y: bottomY,
      width: outerRect.width,
      height: bottomHeight,
    }); // adds the bottom visible strip
  }

  const middleY = Math.max(outerRect.y, innerRect.y); // finds the top edge of the overlapping vertical area
  const middleBottom = Math.min(outerBottom, innerBottom); // finds the bottom edge of the overlapping vertical area
  const middleHeight = middleBottom - middleY; // calculates the height used for the left and right strips

  if (middleHeight > 0) {
    const leftWidth = Math.min(innerRect.x, outerRight) - outerRect.x; // calculates the visible strip left of the inner rectangle

    if (leftWidth > 0) {
      rects.push({
        x: outerRect.x,
        y: middleY,
        width: leftWidth,
        height: middleHeight,
      }); // adds the left visible strip
    }

    const rightX = Math.max(innerRight, outerRect.x); // finds where the right visible strip starts
    const rightWidth = outerRight - rightX; // calculates the visible strip right of the inner rectangle

    if (rightWidth > 0) {
      rects.push({
        x: rightX,
        y: middleY,
        width: rightWidth,
        height: middleHeight,
      }); // adds the right visible strip
    }
  }

  return rects; // returns all visible pieces after subtracting the inner rectangle
}; // creates a visible area for one rectangle minus another rectangle

export const getFoamPreviewRects = (
  selectedFoam,
  cncLayoutWidth,
  cncLayoutHeight
) => {
  const safetyMargin = 8; // defines the required minimum space around the cnc layout in mm

  // database mapping:
  // length = horizontal svg dimension
  // width = vertical svg dimension
  // height = foam thickness and not relevant for 2d preview
  const foamSvgWidth = selectedFoam.length;
  const foamSvgHeight = selectedFoam.width;

  const foamRect = {
    x: (cncLayoutWidth - foamSvgWidth) / 2,
    y: (cncLayoutHeight - foamSvgHeight) / 2,
    width: foamSvgWidth,
    height: foamSvgHeight,
  }; // centers the foam rectangle around the cnc layout

  const clearanceRect = {
    x: -safetyMargin,
    y: -safetyMargin,
    width: cncLayoutWidth + safetyMargin * 2,
    height: cncLayoutHeight + safetyMargin * 2,
  }; // represents the cnc layout plus the required 8mm space on every side

  const extraFoamRects = createRectDifferenceRects(foamRect, clearanceRect); // gets foam areas outside the required clearance
  const missingFoamRects = createRectDifferenceRects(clearanceRect, foamRect); // gets clearance areas that exceed the selected foam

  return { foamRect, clearanceRect, extraFoamRects, missingFoamRects };
}; // calculates the preview rectangles of a foam base around the cnc layout

const packagingSafetyMargin = 16; // adds 8mm clearance on both sides of each axis

export const validatePackagingConsistency = (
  foamBase,
  foamLid,
  cardboardBox,
  foamBaseDocuments,
  foamLidDocuments,
  cardboardBoxes
) => {
  if (!foamBase || !foamLid || !cardboardBox) {
    return 'Foam (base), Foam (lid) und Cardboard Box müssen ausgewählt sein.'; // requires all packaging selections before saving
  }

  const selectedFoamBase = foamBaseDocuments.find(
    (foam) => foam._id === foamBase
  ); // finds the selected foam base document

  const selectedFoamLid = foamLidDocuments.find((foam) => foam._id === foamLid); // finds the selected foam lid document

  const selectedCardboardBox = cardboardBoxes.find(
    (box) => box._id === cardboardBox
  ); // finds the selected cardboard box document

  if (!selectedFoamBase || !selectedFoamLid || !selectedCardboardBox) {
    return 'Die ausgewählten Verpackungsdokumente konnten nicht gefunden werden.'; // blocks saving if any selected document no longer exists
  }

  if (
    selectedFoamBase.size !== selectedFoamLid.size ||
    selectedFoamBase.size !== selectedCardboardBox.size
  ) {
    return (
      `Die Verpackungsgrößen stimmen nicht überein. ` +
      `Foam (base): ${selectedFoamBase.size}, ` +
      `Foam (lid): ${selectedFoamLid.size}, ` +
      `Cardboard Box: ${selectedCardboardBox.size}.`
    ); // blocks saving if the selected packaging components use different sizes
  }

  return null; // confirms that the selected packaging combination is valid
}; // validates that all selected packaging components exist and use the same size

export const calculatePackagingLayoutSize = (layout) => {
  const oneKeycapUnitInMM = 20.1; // cnc milling dimensions

  return {
    width: calculateLayoutWidth(layout, oneKeycapUnitInMM),
    height: calculateLayoutHeight(layout, oneKeycapUnitInMM),
  };
}; // calculates layout dimensions used for packaging selection

export const calculateRequiredFoamBase = (
  layoutWidth,
  layoutHeight,
  foamBaseDocuments
) => {
  const requiredWidth = layoutWidth + packagingSafetyMargin; // adds the required clearance to the cnc layout width
  const requiredHeight = layoutHeight + packagingSafetyMargin; // adds the required clearance to the cnc layout height

  const fittingFoams = foamBaseDocuments.filter((foam) => {
    const fitsNormalOrientation =
      foam.length >= requiredWidth && foam.width >= requiredHeight; // checks whether the foam fits without rotating it

    const fitsRotatedOrientation =
      foam.width >= requiredWidth && foam.length >= requiredHeight; // checks whether the foam fits when rotated by 90 degrees

    return fitsNormalOrientation || fitsRotatedOrientation; // keeps foams that fit in either orientation
  });

  if (fittingFoams.length === 0) {
    return null; // returns nothing if no foam can fit the layout
  }

  const smallestFittingFoam = fittingFoams.sort((a, b) => {
    const areaA = a.length * a.width; // calculates the 2d area of the first foam
    const areaB = b.length * b.width; // calculates the 2d area of the second foam

    return areaA - areaB; // sorts smaller foams before larger foams
  })[0]; // selects the smallest foam that still fits the layout

  return smallestFittingFoam; // returns the best fitting foam base
}; // finds the smallest foam base that physically fits the cnc layout plus clearance

export const getMatchingPackagingDocuments = (
  foamBase,
  foamLidDocuments,
  cardboardBoxes
) => {
  const foamLid = foamLidDocuments.find(
    (foam) => foam.size === foamBase.size && foam.type === 'lid'
  ); // finds the foam lid with the same size as the selected foam base

  const cardboardBox = cardboardBoxes.find((box) => box.size === foamBase.size); // finds the cardboard box with the same size as the selected foam base

  return {
    foamBase: foamBase._id,
    foamLid: foamLid?._id || null,
    cardboardBox: cardboardBox?._id || null,
  }; // returns the matching packaging document ids
}; // finds matching foam lid and cardboard box documents for a foam base

export const calculatePreviewViewBox = ({
  cncWidth,
  cncHeight,
  layoutWidth,
  layoutHeight,
  foam,
}) => {
  const fallbackWidth = cncWidth || layoutWidth; // uses the normal layout width until the cnc width is available
  const fallbackHeight = cncHeight || layoutHeight; // uses the normal layout height until the cnc height is available

  if (!foam) {
    return {
      x: 0,
      y: 0,
      width: fallbackWidth,
      height: fallbackHeight,
    }; // uses the layout dimensions as the viewbox when no foam is selected
  }

  // database mapping:
  // length = svg width
  // width = svg height
  // height = thickness
  const foamSvgWidth = foam.length;
  const foamSvgHeight = foam.width;

  const clearanceWidth = fallbackWidth + 16; // adds the required 8mm clearance to the left and right
  const clearanceHeight = fallbackHeight + 16; // adds the required 8mm clearance to the top and bottom

  const previewWidth = Math.max(fallbackWidth, foamSvgWidth, clearanceWidth); // uses the widest required area for the preview viewbox

  const previewHeight = Math.max(
    fallbackHeight,
    foamSvgHeight,
    clearanceHeight
  ); // uses the tallest required area for the preview viewbox

  return {
    x: (fallbackWidth - previewWidth) / 2,
    y: (fallbackHeight - previewHeight) / 2,
    width: previewWidth,
    height: previewHeight,
  }; // centers the preview viewbox around the cnc layout
}; // the view box of the layout editor's preview: the cnc layout (the plain layout until the cnc size is known), the selected foam base and the 8 mm clearance around the layout, whichever is largest, centred on the layout (LayoutEditScreen, PR 32)
